import React from 'react';
import {
  getDisplayLabel,
  modelListFor,
  providerBase,
  unlistedOption,
} from '../../utils/modelConstants';
import { modelFieldKind } from '../../utils/modelLists';

const PLACEHOLDER_STYLE = { color: '#999' };

/** What the free-text field asks for, by provider. */
const TYPED_PLACEHOLDERS = {
  aws: 'Enter Bedrock model ID',
  azure: 'Deployment name',
};

/**
 * The model field for one provider key, the same wherever a model is
 * picked: the left panel, and the workflow builder's node and sub-agent
 * pickers (`modelFieldKind` decides which field):
 *
 * - a dropdown of the platform catalog plus the user's custom models
 *   (`modelListFor`), the recommended one labelled;
 * - the platform's aliases for Stackform;
 * - a local key's installed device models, or why there are none;
 * - free text with those suggestions for Azure and AWS, whose model
 *   names are the user's own.
 *
 * A saved model the list no longer offers stays selected, labelled
 * "(removed)" once the catalog and the user's custom models have loaded
 * (`unlistedOption`), so the field never shows a model other than the one
 * that runs.
 *
 * @param {object}   props
 * @param {string}   props.providerKey - E.g. "openai_2", "local", "stackform".
 * @param {string}   [props.value]     - The chosen model.
 * @param {Function} props.onChange    - Called with the new model ('' for none).
 * @param {object}   [props.modelLists] - `{catalog, availableModels,
 *   recommended, localModels, localModelsLoaded}` (useModelSettings);
 *   `catalog` and `availableModels` are null until they load.
 * @param {string}   [props.ariaLabel] - The field's accessible name.
 * @param {string}   props.listId      - A unique id for the suggestions.
 * @param {string}   [props.emptyLabel] - The empty choice's label, or the
 *   free-text field's placeholder.
 * @param {object}   [props.selectProps] - Extra props for a dropdown.
 * @param {object}   [props.inputProps]  - Extra props for the text field.
 */
export default function ModelSelect({
  providerKey,
  value,
  onChange,
  modelLists = {},
  ariaLabel,
  listId,
  emptyLabel,
  selectProps = {},
  inputProps = {},
}) {
  const {
    catalog = null,
    availableModels = {},
    recommended = {},
    localModels = {},
    localModelsLoaded = false,
  } = modelLists;
  const kind = modelFieldKind(providerKey);
  const current = value || '';
  const handle = (event) => onChange(event.target.value);

  if (kind === 'local') {
    const models = localModels[providerKey] || [];
    if (!models.length) {
      return (
        <select {...selectProps} aria-label={ariaLabel} value="" disabled>
          <option value="">
            {localModelsLoaded
              ? 'Device offline — no models available'
              : 'Loading models…'}
          </option>
        </select>
      );
    }
    const missing = current && !models.includes(current);
    return (
      <select
        {...selectProps}
        aria-label={ariaLabel}
        value={current}
        onChange={handle}
      >
        <option value="" style={PLACEHOLDER_STYLE}>
          {emptyLabel || 'Select a model'}
        </option>
        {models.map((model) => (
          <option key={model} value={model}>{model}</option>
        ))}
        {missing && (
          <option value={current}>{`${current} (not on this device)`}</option>
        )}
      </select>
    );
  }

  const base = kind === 'platform' ? 'default' : providerBase(providerKey);
  const { all } = modelListFor(base, { catalog, availableModels });
  const options = all.map((model) => {
    const label = getDisplayLabel(base, model);
    return {
      value: model,
      label: model === recommended[base] ? `${label} (recommended)` : label,
    };
  });

  if (kind === 'typed') {
    return (
      <>
        <input
          {...inputProps}
          type="text"
          aria-label={ariaLabel}
          list={options.length ? listId : undefined}
          value={current}
          onChange={handle}
          placeholder={emptyLabel || TYPED_PLACEHOLDERS[base]}
        />
        {options.length > 0 && (
          <datalist id={listId}>
            {options.map(({ value: model, label }) => (
              <option key={model} value={model}>{label}</option>
            ))}
          </datalist>
        )}
      </>
    );
  }

  const unlisted = unlistedOption(all, current, {
    catalogLoaded: catalog !== null && availableModels !== null,
  });
  // The platform's aliases are known before the catalog loads; nothing else.
  const placeholder = kind === 'list' && catalog === null
    ? 'Loading models…'
    : 'Select a model';
  return (
    <select
      {...selectProps}
      aria-label={ariaLabel}
      value={current}
      onChange={handle}
    >
      <option value="" style={PLACEHOLDER_STYLE}>
        {emptyLabel || placeholder}
      </option>
      {options.map(({ value: model, label }) => (
        <option key={model} value={model}>{label}</option>
      ))}
      {unlisted && <option value={unlisted.value}>{unlisted.label}</option>}
    </select>
  );
}
